import { TeamMemberInputComponent } from '../../input/team-member-input/team-member-input.component';
import { TeamMember } from '../../model/team/TeamMember';
import { ProjectListService } from '../../services/project-list/project-list.service';
import { AuthService } from 'src/app/auth/auth.service';
import { labelToTaskStatus, taskStatusToLabel } from '../../model/task/taskStatus';
import { MatDialog } from '@angular/material/dialog';
import { UserService } from '../../services/user/user.service';
import { DragulaService } from 'ng2-dragula';
import { ProjectDetailsService } from '../../services/project-details/project-details.service';
import { TaskService } from '../../services/task/task.service';
import { FormGroup, FormBuilder, FormControl } from '@angular/forms';
import { TaskLabel, taskLabelToLabel } from '../../model/task/taskLabel';
import { TaskStatus } from 'src/app/shared/model/task/taskStatus';
import { GetProjects } from '../../services/project-list/GetProjects';
import { Subject, takeUntil, lastValueFrom } from 'rxjs';
import {Component, Input, OnDestroy, OnInit, ViewChild} from '@angular/core';
import { Task } from '../../model/task/task';
import { TaskDetailsComponent } from './task-details/task-details.component';

@Component({
  selector: 'digires-task-list',
  templateUrl: './task-list.component.html',
  styleUrls: ['./task-list.component.scss']
})
export class TaskListComponent implements OnInit, OnDestroy {


  destroy$: Subject<boolean> = new Subject<boolean>();
  @ViewChild(TeamMemberInputComponent) teamMemberInputComponent: TeamMemberInputComponent;
  @Input() project: GetProjects;

  statusList = Object.keys(TaskStatus).filter(s => s != TaskStatus.DELETED);
  labels = Object.keys(TaskLabel);

  allTasks: Task[] = [];
  toDoTasks: Task[] = [];
  inProgressTasks: Task[] = [];
  doneTasks: Task[] = [];

  teamMembers: TeamMember[] = [];
  myProjects: GetProjects[] = [];

  taskFormGroup: FormGroup;
  isFilterActiv = false;

  isReadOnly = false;
  isSaving = false;

  constructor(
    private taskService: TaskService,
    private projectDetailsService: ProjectDetailsService,
    private projectListService: ProjectListService,
    private dragulaService: DragulaService,
    private userService: UserService,
    private authService: AuthService,
    private fb: FormBuilder,
    private dialog: MatDialog,
  ) {
    dragulaService.drag("nested-bag").pipe(takeUntil(this.destroy$)).subscribe(({ el }) => {
      this.removeClass(el, 'ex-moved');
    });
    dragulaService.dropModel("nested-bag").pipe(takeUntil(this.destroy$)).subscribe(({  el, target, item }) => {
      this.addClass(el, 'ex-moved');
      this.updateTaskLabel(item, labelToTaskStatus[target.id]);
    });
    dragulaService.over("nested-bag").pipe(takeUntil(this.destroy$)).subscribe(({ el, container }) => {
      this.addClass(container, 'ex-over');
    });
    dragulaService.out("nested-bag").pipe(takeUntil(this.destroy$)).subscribe(({ el, container }) => {
      this.removeClass(container, 'ex-over');
    });
  }

  private hasClass(el: any, name: string) {
    return new RegExp('(?:^|\\s+)' + name + '(?:\\s+|$)').test(el.className);
  }

  private addClass(el: any, name: string) {
    if (!this.hasClass(el, name)) {
      el.className = el.className ? [el.className, name].join(' ') : name;
    }
  }

  private removeClass(el: any, name: string) {
    if (this.hasClass(el, name)) {
      el.className = el.className.replace(new RegExp('(?:^|\\s+)' + name + '(?:\\s+|$)', 'g'), '');
    }
  }

  async ngOnInit(): Promise<void> {

    this.isReadOnly = !this.authService.canEditTaskItem(this.project, null);

    this.taskFormGroup = this.fb.group({
      selectedLabel: new FormControl(null),
      selectedOwner: new FormControl(null),
      selectedProject: new FormControl(null)
    });

    this.taskFormGroup.valueChanges.pipe(
      takeUntil(this.destroy$)).subscribe(() => {
      this.filterLabel();
    });

    if(this.project) {
      this.teamMembers = (await this.projectDetailsService.fetchTeam(this.project.projectId)).members;
      await this.loadTasksForProject();
    } else {
      const teamMembership = await this.userService.fetchTeamMembership(this.authService.getUserId());
      const projectSet = new Set<string>();
      for (const p of teamMembership) {
        projectSet.add(p.projectId);
      }
      this.myProjects = (await lastValueFrom(this.projectListService.fetchProjectsByIds([...projectSet]))).loadedElements
      await this.loadTasksForUser();
    }
  }

  ngOnDestroy() {
    if (this.dragulaService.find('nested-bag')) {
      this.dragulaService.destroy('nested-bag');
    }
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

  async loadTasksForProject() {
    this.allTasks = await this.taskService.getAllTasksForProject(this.project.projectId);
    this.segregateTasks();
  }

  async loadTasksForUser() {
    this.allTasks = await this.taskService.getAllTasksForUser();
    this.segregateTasks();
  }

  private segregateTasks() {
    const label: string = this.taskFormGroup.value.selectedLabel;
    const owner = this.taskFormGroup.value.selectedOwner;
    this.toDoTasks = this.filterTasks(TaskStatus.TO_DO, label, owner?.userId);
    this.inProgressTasks = this.filterTasks(TaskStatus.IN_PROGRESS, label, owner?.userId);
    this.doneTasks = this.filterTasks(TaskStatus.DONE, label, owner?.userId);
  }

  private filterTasks(status: TaskStatus, label: string, ownerId: string) {
    return this.allTasks.filter( task => task.status === status
      && (!label || task.label === label)
      && (!ownerId || task.ownerId === ownerId)
  	);
  }

  openDialog(task?: Task) {
    let copyTask = null;
    if (task) {
      copyTask = Object.assign(new Task(), task);
    }

    this.dialog
      .open(TaskDetailsComponent, {
        hasBackdrop: true,
        width: '90vw',
        data: {
          task: copyTask,
          project: this.project,
          teamMembers: this.teamMembers,
          statusList: this.statusList,
          labels: this.labels
        }
      })
      .afterClosed()
      .subscribe(async result => {
        if (result) {
          if (this.project) {
            await this.loadTasksForProject();
          } else {
            await this.loadTasksForUser();
          }
        }
      });
  }

  filterLabel() {
    if (
      this.taskFormGroup.value.selectedLabel !== null ||
      this.taskFormGroup.value.selectedOwner !== null ||
      this.taskFormGroup.value.selectedProject !== null
    ) {
      this.segregateTasks();
      this.isFilterActiv = true;
    }
  }

  resetFilter() {
    this.taskFormGroup.patchValue({
        selectedLabel: null,
        selectedOwner: null,
        selectedProject: null
    });
    this.teamMemberInputComponent?.reset();
    this.taskFormGroup.reset();
    this.segregateTasks();

    this.isFilterActiv = false;
  }

  updateTaskLabel(task: Task, newStatus: TaskStatus): void {
    if (task) {
      task.status = newStatus;
      this.updateTask(task);
    }
  }

  private async updateTask(task: Task) {
    if (!this.isSaving) {
      this.isSaving = true;
      await this.taskService.updateTask(task);
    }
    this.isSaving = false;
  }

  statusToLabel(status: TaskStatus) {
    return taskStatusToLabel[status];
  }

  labelToLabel(label: TaskLabel) {
    return taskLabelToLabel[label];
  }

  getProjectName(project: GetProjects) {
    return project.name;
  }
}
